/*
 * Inka — chips de categorías fijos (agente C).
 *
 * Home (data-home="true"):
 *   - Cada chip apunta a #cat-<handle> (bloque de categoría de la carta, agente D).
 *   - Con data-reveal="after_hero" la barra aparece al pasar el hero: [data-inka-hero] / .inka-hero
 *     o, si no existe, la primera sección de #MainContent.
 *   - Scrollspy: marca (clase is-active + aria-current="true") la categoría visible y desplaza el chip a la vista.
 *   - Clic: scroll suave compensando cabecera + chips. Si el ancla no existe, va a la colección (data-fallback).
 *   - Al cargar con #cat-x o ?cat=x, recoloca el scroll con la compensación correcta.
 * Resto de páginas: solo desplaza a la vista el chip activo (colección actual).
 *
 * Variables que publica en <body>: --inka-chips-height, --inka-chips-offset (cabecera + chips).
 * Clase en <body>: inka-chips-visible (cuando la barra se ve).
 * Evento en document: 'inka:chips-active' { detail: { handle, id } } cuando cambia la categoría activa.
 *
 * Contenedor de scroll: Horizon usa .page-wrapper en escritorio (≥990px) y el documento en móvil.
 */
import {
  getScrollContainer,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class InkaChips extends HTMLElement {
  #scrollTarget = null;
  #raf = null;
  #lockUntil = 0;
  #activeId = null;
  #visible = null;

  connectedCallback() {
    this.bar = this.querySelector('.inka-chips__bar');
    this.list = this.querySelector('.inka-chips__list');
    this.chips = Array.from(this.querySelectorAll('a.inka-chips__chip'));
    this.isHome = this.dataset.home === 'true';
    this.reveal = this.isHome && this.dataset.reveal === 'after_hero';

    if (!this.list || this.chips.length === 0) return;

    this.#resizeObserver = new ResizeObserver(() => this.#publishSizes());
    this.#resizeObserver.observe(this);
    const header = document.querySelector('#header-component');
    if (header) this.#resizeObserver.observe(header);

    if (!this.isHome) {
      this.#setVisible(true);
      const current = this.chips.find((c) => c.classList.contains('is-active'));
      if (current) this.#centerChip(current, false);
      return;
    }

    this.#setVisible(!this.reveal);
    this.addEventListener('click', this.#onClick);
    this.#bindScroll();
    scrollContainerMediaQuery.addEventListener('change', this.#onBreakpoint);
    this.#update();

    // Llegada con #cat-x o ?cat=x (desde otra página): recolocar con la compensación correcta.
    const fromHash = decodeURIComponent(window.location.hash.slice(1));
    const fromQuery = new URLSearchParams(window.location.search).get('cat');
    const id = fromHash.startsWith('cat-') ? fromHash : fromQuery ? `cat-${fromQuery}` : null;
    if (id && document.getElementById(id)) {
      window.addEventListener(
        'load',
        () => requestAnimationFrame(() => this.scrollToId(id, false)),
        { once: true }
      );
    }
  }

  disconnectedCallback() {
    this.#unbindScroll();
    this.removeEventListener('click', this.#onClick);
    scrollContainerMediaQuery.removeEventListener('change', this.#onBreakpoint);
    this.#resizeObserver?.disconnect();
  }

  #resizeObserver = null;

  /* ---------- medidas ---------- */

  #headerHeight() {
    const header = document.querySelector('#header-component');
    return header ? header.getBoundingClientRect().height : 0;
  }

  #barHeight() {
    return this.bar ? this.bar.getBoundingClientRect().height : 0;
  }

  #offset() {
    return Math.round(this.#headerHeight() + this.#barHeight() + 8);
  }

  #publishSizes() {
    const body = document.body.style;
    body.setProperty('--inka-chips-height', `${Math.round(this.#barHeight())}px`);
    body.setProperty('--inka-chips-offset', `${this.#offset()}px`);
  }

  /* ---------- scroll ---------- */

  #bindScroll() {
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
    if ('onscrollend' in window) this.#scrollTarget.addEventListener('scrollend', this.#onScrollEnd);
  }

  #unbindScroll() {
    if (!this.#scrollTarget) return;
    this.#scrollTarget.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget.removeEventListener('scrollend', this.#onScrollEnd);
    this.#scrollTarget = null;
  }

  #onBreakpoint = () => {
    this.#unbindScroll();
    this.#bindScroll();
    this.#update();
  };

  #onScroll = () => {
    if (this.#raf !== null) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = null;
      this.#update();
    });
  };

  #onScrollEnd = () => {
    if (this.#lockUntil) {
      this.#lockUntil = 0;
      this.#update();
    }
  };

  #update() {
    if (this.reveal) this.#setVisible(this.#pastHero());
    if (Date.now() < this.#lockUntil) return;
    this.#spy();
  }

  #pastHero() {
    const hero =
      document.querySelector('[data-inka-hero], .inka-hero') ||
      document.querySelector('#MainContent > .shopify-section');
    if (!hero) return true;
    const header = document.querySelector('#header-component');
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    return hero.getBoundingClientRect().bottom <= headerBottom + 1;
  }

  #setVisible(visible) {
    if (visible === this.#visible) return;
    this.#visible = visible;
    this.classList.toggle('is-visible', visible);
    document.body.classList.toggle('inka-chips-visible', visible);
    if (this.bar) {
      if (visible) this.bar.removeAttribute('inert');
      else this.bar.setAttribute('inert', '');
    }
    this.#publishSizes();
  }

  #spy() {
    const line = this.#offset() + 4;
    let current = null;
    let best = -Infinity;
    for (const chip of this.chips) {
      const el = document.getElementById(chip.dataset.target);
      if (!el) continue;
      const top = el.getBoundingClientRect().top;
      // La última categoría cuyo inicio ya pasó la línea (no depende del orden del DOM).
      if (top <= line && top > best) {
        best = top;
        current = chip;
      }
    }

    // Final de la página: si la última categoría ya no puede subir más, se marca igualmente.
    const container = getScrollContainer();
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 2) {
      for (let i = this.chips.length - 1; i >= 0; i--) {
        const el = document.getElementById(this.chips[i].dataset.target);
        if (el && el.getBoundingClientRect().top < window.innerHeight) {
          current = this.chips[i];
          break;
        }
      }
    }

    this.#setActive(current);
  }

  #setActive(chip) {
    const id = chip ? chip.dataset.target : null;
    if (id === this.#activeId) return;
    this.#activeId = id;
    for (const c of this.chips) {
      const on = c === chip;
      c.classList.toggle('is-active', on);
      if (on) c.setAttribute('aria-current', 'true');
      else c.removeAttribute('aria-current');
    }
    if (chip) this.#centerChip(chip, !reduceMotion());
    document.dispatchEvent(
      new CustomEvent('inka:chips-active', { detail: { id, handle: id ? id.replace(/^cat-/, '') : null } })
    );
  }

  #centerChip(chip, smooth) {
    const list = this.list;
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const left = chip.offsetLeft - (list.clientWidth - chip.offsetWidth) / 2;
    list.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' });
  }

  /* ---------- navegación ---------- */

  #onClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('a.inka-chips__chip') : null;
    if (!chip || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    const id = chip.dataset.target;
    const el = id ? document.getElementById(id) : null;
    event.preventDefault();
    if (!el) {
      if (chip.dataset.fallback) window.location.href = chip.dataset.fallback;
      return;
    }
    this.scrollToId(id, !reduceMotion());
    this.#setActive(chip);
    try {
      history.replaceState(history.state, '', `#${id}`);
    } catch (_) {
      /* sin history: no pasa nada */
    }
  };

  /**
   * Desplaza la página hasta el bloque de categoría, dejando sitio a cabecera + chips.
   * @param {string} id
   * @param {boolean} smooth
   */
  scrollToId(id, smooth = true) {
    const el = document.getElementById(id);
    if (!el) return;
    const container = getScrollContainer();
    const isDoc = container === document.scrollingElement || container === document.documentElement;
    const containerTop = isDoc ? 0 : container.getBoundingClientRect().top;
    const top = container.scrollTop + el.getBoundingClientRect().top - containerTop - this.#offset();
    this.#lockUntil = smooth ? Date.now() + 1200 : 0;
    container.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'instant' });
    if (!smooth) this.#update();

    // Enfoca el bloque para lectores de pantalla y teclado sin volver a desplazar.
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  }
}

if (!customElements.get('inka-chips')) customElements.define('inka-chips', InkaChips);
